import {
  Box,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { getTeamData, isMongoUnavailableError, searchByName, type EntrySearchResult } from 'api/team'
import { colors } from 'constants/colors'
import React, { type Dispatch, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { generalRoutes } from 'Routing'
import { setTeamData } from 'store/slices/userSlice'
import type { TeamData } from 'types/fpl/teams'
import { redirect } from 'utils/general'

type Mode = 'id' | 'name'

const DEBOUNCE_MS = { id: 500, name: 800 }

const LoginCard: React.FC = () => {
  const dispatch: Dispatch<any> = useDispatch()
  const navigate: NavigateFunction = useNavigate()

  const [mode, setMode] = useState<Mode>('id')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<EntrySearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [loggingIn, setLoggingIn] = useState(false)
  const [error, setError] = useState('')

  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    setResults([])
    setError('')

    // cancel any in-flight request immediately
    abortRef.current?.abort()
    abortRef.current = null

    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!query.trim()) return

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController()
      abortRef.current = controller

      setSearching(true)
      try {
        if (mode === 'id') {
          const id = parseInt(query.trim(), 10)
          if (isNaN(id)) { setError('Enter a valid number'); return }
          const team: TeamData = await getTeamData(id, controller.signal)
          if (team?.id) {
            setResults([{ entry: team.id, entry_name: team.name, player_name: `${team.player_first_name} ${team.player_last_name}` }])
          } else {
            setError('Team not found')
          }
        } else {
          if (query.trim().length < 2) return
          const found = await searchByName(query.trim(), controller.signal)
          setResults(found)
          if (found.length === 0) setError('No teams found')
        }
      } catch (e: any) {
        if (e?.code === 'ERR_CANCELED') { /* aborted — ignore */ }
        else if (mode === 'name' && isMongoUnavailableError(e)) {
          setError('Team name search is unavailable right now. Please sign in with your Team ID.')
        }
        else if (e?.response?.status === 404) setError('Team not found')
        else if (e?.response?.status >= 500 || e?.code === 'ERR_NETWORK' || e?.code === 'ECONNABORTED') {
          setError('Server is starting up, please try again in a few seconds')
        }
        else setError('Something went wrong')
      } finally {
        setSearching(false)
      }
    }, DEBOUNCE_MS[mode])

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      abortRef.current?.abort()
    }
  }, [query, mode])

  const handleModeChange = (_: React.MouseEvent, newMode: Mode | null) => {
    if (!newMode) return
    setMode(newMode)
    setQuery('')
    setResults([])
    setError('')
  }

  const loginWith = async (entry: EntrySearchResult) => {
    setLoggingIn(true)
    setError('')
    try {
      const teamData: TeamData = await getTeamData(entry.entry)
      if (!teamData?.id) throw new Error()
      dispatch(setTeamData(teamData))
      redirect(navigate, generalRoutes.HOME)
    } catch (e: any) {
      if (e?.response?.status >= 500 || e?.code === 'ERR_NETWORK' || e?.code === 'ECONNABORTED') {
        setError('Server is starting up, please try again in a few seconds')
      } else {
        setError('Failed to sign in')
      }
    } finally { setLoggingIn(false) }
  }

  return (
    <Paper
      elevation={8}
      sx={{
        flex: 1,
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: 'none',
        backgroundColor: isDark ? colors.ALMOST_BLACK : colors.WHITE,
      }}
    >
      <Typography
        fontFamily='PremierLeagueBold'
        component='h2'
        sx={{ color: isDark ? colors.WHITE : colors.ALMOST_BLACK, textAlign: 'center', mb: 2.5, fontSize: '1.35rem' }}
      >
        Access Your Mini-League
      </Typography>

      <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size='medium' sx={{ mb: 2.5, width: '100%' }}>
        {(['id', 'name'] as Mode[]).map(m => (
          <ToggleButton
            key={m}
            value={m}
            sx={{
              flex: 1,
              fontFamily: 'PremierLeagueBold',
              fontSize: '0.8rem',
              '&.Mui-selected': { bgcolor: colors.MOONSTONE, color: colors.WHITE, '&:hover': { bgcolor: colors.MOONSTONE } },
            }}
          >
            {m === 'id' ? 'Team ID' : 'Team Name'}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ width: '100%', position: 'relative' }}>
        <TextField
          key={mode}
          fullWidth
          autoFocus
          label={mode === 'id' ? 'Enter your Team ID' : 'Enter your team or manager name'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          inputProps={mode === 'id' ? { inputMode: 'numeric', pattern: '[0-9]*' } : {}}
          InputProps={{ endAdornment: searching ? <CircularProgress size={16} /> : null }}
        />

        {results.length > 0 && (
          <Paper variant='outlined' sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, maxHeight: 220, overflowY: 'auto', mt: 0.5 }}>
            <List dense disablePadding>
              {results.map((r, i) => (
                <React.Fragment key={r.entry}>
                  {i > 0 && <Divider />}
                  <ListItemButton onClick={() => { void loginWith(r) }} disabled={loggingIn}>
                    <ListItemText
                      primary={<Typography fontFamily='PremierLeagueBold' fontSize='0.875rem'>{r.entry_name}</Typography>}
                      secondary={<Typography fontFamily='PremierLeagueRegular' fontSize='0.75rem' color='text.secondary'>{r.player_name} · ID {r.entry}</Typography>}
                    />
                    {loggingIn && <CircularProgress size={14} sx={{ ml: 1 }} />}
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      <Box sx={{ minHeight: 24, mt: 0.75 }}>
        <Typography fontFamily='PremierLeagueRegular' fontSize='0.8rem' color='error'>
          {error}
        </Typography>
      </Box>

      {/* Push footer to bottom and lock its height so the card never resizes */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {mode === 'id' && (
            <Typography fontFamily='PremierLeagueBold' fontSize='0.8rem' color='text.secondary' align='center'>
              Can't find your Team ID?{' '}
              <Link href='https://fpl.team/find-fpl-team-id/' target='_blank' rel='noopener noreferrer' color={colors.MOONSTONE}>
                Find it here
              </Link>
            </Typography>
          )}
          {mode === 'name' && (
            <Typography fontFamily='PremierLeagueRegular' fontSize='0.75rem' color='text.secondary' align='center'>
              Team Name search is available on the following regions: <b>Israel</b>.
              <br />
              If name search is unavailable, sign in with your Team ID instead.
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  )
}

export default LoginCard
